// src/ApiPanel/components/reports/ReportKit.jsx
// The shared vocabulary every CA/GST report page is built from. Fifteen
// screens that each hand-rolled their own table and filter bar would drift
// apart within a month; this keeps them one thing.
//
// Money is formatted, never computed, on the client. Every figure here is a
// string the API already rounded — parsing it into a JS number to add things
// up would reintroduce the float error the backend went to some trouble to
// avoid, and would let the browser disagree with the ledger.
import React from "react";

/* ── formatting ─────────────────────────────────────────────────────────── */

export function money(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function rupees(value) {
  const formatted = money(value);
  return formatted === "—" ? formatted : `₹${formatted}`;
}

export function count(value) {
  const n = Number(value ?? 0);
  return Number.isNaN(n) ? "0" : n.toLocaleString("en-IN");
}

export function prettyDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function daysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function currentPeriod() {
  return new Date().toISOString().slice(0, 7);
}

/* ── page scaffold ──────────────────────────────────────────────────────── */

export function ReportPage({ title, subtitle, actions, children }) {
  return (
    <div className="apip-report-page">
      <div className="apip-page-head">
        <div>
          <h1 className="apip-page-title">{title}</h1>
          {subtitle && <p className="apip-page-sub">{subtitle}</p>}
        </div>
        {actions && <div className="apip-page-actions">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function Card({ title, right, children, className = "" }) {
  return (
    <div className={`apip-card ${className}`}>
      {(title || right) && (
        <div className="apip-card-header">
          <span>{title}</span>
          {right && <span className="apip-card-header-right">{right}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

/* ── filters ────────────────────────────────────────────────────────────── */

export function FilterBar({ onApply, onReset, busy, children }) {
  return (
    <form
      className="apip-filterbar"
      onSubmit={(e) => {
        e.preventDefault();
        onApply?.();
      }}
    >
      {children}
      <div className="apip-filter-actions">
        <button type="submit" className="apip-btn apip-btn-primary" disabled={busy}>
          <i className="iconoir-search" /> {busy ? "Loading…" : "Apply"}
        </button>
        {onReset && (
          <button type="button" className="apip-btn apip-btn-ghost" onClick={onReset}>
            Reset
          </button>
        )}
      </div>
    </form>
  );
}

export function Field({ label, hint, children, wide }) {
  return (
    <label className={`apip-filter-field${wide ? " apip-filter-field-wide" : ""}`}>
      <span className="apip-filter-label">{label}</span>
      {children}
      {hint && <span className="apip-filter-hint">{hint}</span>}
    </label>
  );
}

export function DateField({ label, value, onChange, max, min }) {
  return (
    <Field label={label}>
      <input
        type="date"
        className="apip-input"
        value={value || ""}
        max={max}
        min={min}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function MonthField({ label, value, onChange, max }) {
  return (
    <Field label={label}>
      <input
        type="month"
        className="apip-input"
        value={value || ""}
        max={max}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function SelectField({ label, value, onChange, options, placeholder = "All" }) {
  return (
    <Field label={label}>
      <select className="apip-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function TextField({ label, value, onChange, placeholder, hint, maxLength }) {
  return (
    <Field label={label} hint={hint}>
      <input
        type="text"
        className="apip-input"
        value={value || ""}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

/* ── summary tiles ──────────────────────────────────────────────────────── */

export function StatGrid({ children }) {
  return <div className="apip-stat-grid">{children}</div>;
}

export function Stat({ label, value, tone = "", sub }) {
  return (
    <div className={`apip-stat-tile${tone ? ` apip-stat-${tone}` : ""}`}>
      <span className="apip-stat-tile-label">{label}</span>
      <span className="apip-stat-tile-value">{value}</span>
      {sub && <span className="apip-stat-tile-sub">{sub}</span>}
    </div>
  );
}

/* ── table ──────────────────────────────────────────────────────────────── */

/**
 * @param columns  [{ key, label, align, render?(row), width?, numeric? }]
 * @param rows     array of objects
 * @param footer   optional array of cells rendered as a totals row
 */
export function DataTable({ columns, rows, loading, error, empty, footer, onRowClick, rowKey }) {
  if (loading) return <TableSkeleton columns={columns.length} />;
  if (error) return <ErrorState message={error} />;
  if (!rows?.length) return <EmptyState message={empty} />;

  return (
    <div className="apip-table-wrap">
      <table className="apip-table apip-table-report">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={col.numeric || col.align === "right" ? "apip-num" : ""}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey ? rowKey(row, i) : row.id ?? i}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={onRowClick ? "apip-row-clickable" : ""}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={col.numeric || col.align === "right" ? "apip-num" : ""}
                >
                  {col.render ? col.render(row) : row[col.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr>
              {footer.map((cell, i) => (
                <td key={i} className={cell?.numeric ? "apip-num" : ""}>
                  {cell?.value ?? cell ?? ""}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function TableSkeleton({ columns }) {
  return (
    <div className="apip-table-wrap">
      <table className="apip-table apip-table-report">
        <tbody>
          {Array.from({ length: 6 }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: columns }).map((__, c) => (
                <td key={c}>
                  <span className="apip-skel" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EmptyState({ message, icon = "iconoir-page-flip" }) {
  return (
    <div className="apip-empty">
      <i className={icon} />
      <p>{message || "Nothing to show for these filters."}</p>
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="apip-errorstate" role="alert">
      <i className="iconoir-warning-triangle" />
      <p>{message}</p>
      {onRetry && (
        <button type="button" className="apip-btn apip-btn-ghost" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/* ── pagination ─────────────────────────────────────────────────────────── */

export function Pagination({ meta, onPage }) {
  if (!meta || !meta.total_pages || meta.total_pages <= 1) {
    return meta?.total_rows ? (
      <div className="apip-pagination">
        <span className="apip-pag-info">{count(meta.total_rows)} rows</span>
      </div>
    ) : null;
  }
  return (
    <div className="apip-pagination">
      <span className="apip-pag-info">
        Page {meta.page} of {meta.total_pages} · {count(meta.total_rows)} rows
      </span>
      <div className="apip-pag-btns">
        <button
          type="button"
          className="apip-btn apip-btn-ghost"
          disabled={!meta.has_prev}
          onClick={() => onPage(meta.page - 1)}
        >
          <i className="iconoir-nav-arrow-left" /> Prev
        </button>
        <button
          type="button"
          className="apip-btn apip-btn-ghost"
          disabled={!meta.has_next}
          onClick={() => onPage(meta.page + 1)}
        >
          Next <i className="iconoir-nav-arrow-right" />
        </button>
      </div>
    </div>
  );
}

/* ── badges ─────────────────────────────────────────────────────────────── */

const TONES = {
  ISSUED: "ok", PAID: "ok", APPROVED: "ok", COMMITTED: "ok", FILED: "ok",
  SUCCESS: "ok", REVERSED: "ok", VALIDATED: "ok",
  PENDING: "warn", SUBMITTED: "warn", DRAFT: "warn", OPEN: "warn",
  UPLOADED: "warn", LOCKED: "warn",
  CANCELLED: "bad", REJECTED: "bad", FAILED: "bad",
};

export function Badge({ value, tone }) {
  if (!value) return <span className="apip-badge">—</span>;
  const resolved = tone || TONES[String(value).toUpperCase()] || "neutral";
  return <span className={`apip-badge apip-badge-${resolved}`}>{value}</span>;
}

/* ── buttons ────────────────────────────────────────────────────────────── */

export function ExportButton({ onExport, busy, label = "Export CSV" }) {
  return (
    <button type="button" className="apip-btn apip-btn-ghost" onClick={onExport} disabled={busy}>
      <i className="iconoir-download" /> {busy ? "Preparing…" : label}
    </button>
  );
}

/* ── modal ──────────────────────────────────────────────────────────────── */

export function Modal({ open, title, onClose, children, footer, wide }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="apip-modal-backdrop" onMouseDown={onClose}>
      <div
        className={`apip-modal${wide ? " apip-modal-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="apip-modal-head">
          <h3>{title}</h3>
          <button type="button" className="apip-icon-btn" onClick={onClose} aria-label="Close">
            <i className="iconoir-xmark" />
          </button>
        </div>
        <div className="apip-modal-body">{children}</div>
        {footer && <div className="apip-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ── notices ────────────────────────────────────────────────────────────── */

export function Notice({ tone = "info", children }) {
  const icon =
    tone === "warn" ? "iconoir-warning-triangle"
    : tone === "bad" ? "iconoir-warning-circle"
    : tone === "ok" ? "iconoir-check-circle"
    : "iconoir-info-circle";
  return (
    <div className={`apip-notice apip-notice-${tone}`}>
      <i className={icon} />
      <div>{children}</div>
    </div>
  );
}
